// src/utils/firestoreTrades.js
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  orderBy,
  limit,
} from "firebase/firestore";
import { isTradeAvailable } from "./tradeAvailability";

/**
 * Sponsored trades:
 * A trade is "sponsored" if promotions.topAd is set (days)
 * and now <= createdAt + topAd days.
 *
 * ALSO: only return AVAILABLE trades (not locked/accepted).
 */
export const fetchPrivateSponsoredTrades = async () => {
  const toDateSafe = (v) => {
    if (!v) return null;
    if (typeof v?.toDate === "function") return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number" && Number.isFinite(v)) return new Date(v);
    return null;
  };

  const toDaysSafe = (v) => {
    const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const isActiveTopAd = (trade, nowMs) => {
    const promo = trade?.promotions || {};

    const explicitUntil =
      toDateSafe(promo.topAdUntil) ||
      toDateSafe(promo.sponsoredUntil) ||
      toDateSafe(trade?.topAdUntil) ||
      toDateSafe(trade?.sponsoredUntil);

    if (explicitUntil) {
      return nowMs <= explicitUntil.getTime();
    }

    const days =
      toDaysSafe(promo.topAdDays) ||
      toDaysSafe(promo.sponsoredDays) ||
      toDaysSafe(trade?.topAdDays) ||
      toDaysSafe(trade?.sponsoredDays) ||
      toDaysSafe(promo.topAd);

    const createdAtDate = toDateSafe(trade?.createdAt);
    if (!days || !createdAtDate) return false;

    const expiryMs = createdAtDate.getTime() + days * 24 * 60 * 60 * 1000;
    return nowMs <= expiryMs;
  };

  try {
    const nowMs = Date.now();

    const q1 = query(
      collection(db, "Trades"),
      where("promotions.topAd", ">", 0),
      orderBy("promotions.topAd"),
      orderBy("createdAt", "desc"),
      limit(200)
    );

    const snap = await getDocs(q1);

    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter(isTradeAvailable)
      .filter((t) => isActiveTopAd(t, nowMs));
  } catch (error) {
    // Fallback: full scan but safe
    try {
      console.warn(
        "⚠️ Sponsored query failed; falling back to full scan:",
        error?.message || error
      );

      const nowMs = Date.now();
      const snap = await getDocs(collection(db, "Trades"));

      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(isTradeAvailable)
        .filter((t) => isActiveTopAd(t, nowMs));
    } catch (fallbackError) {
      console.error("❌ Error fetching sponsored trades (fallback failed):", fallbackError);
      return [];
    }
  }
};

/**
 * Add trade listing:
 * Aligns to your latest Firestore rules + AddTrade payload.
 *
 * IMPORTANT:
 * - Rules require request.resource.data.ownerId == request.auth.uid
 * - Therefore we MUST write ownerId (even if you also store ownerUid).
 *
 * ALSO:
 * - Writes isAvailable=true so new trades can be filtered efficiently later.
 */
export const addTradeListing = async (tradeData) => {
  try {
    if (!tradeData || typeof tradeData !== "object") {
      throw new Error("tradeData is required.");
    }

    const ownerId = String(tradeData.ownerId || "").trim();
    if (!ownerId) {
      throw new Error("ownerId is required (must equal auth.uid).");
    }

    const loc = tradeData.location || {};
    const city =
      (typeof loc.city === "string" && loc.city.trim()) ||
      (typeof loc.cityLabel === "string" && loc.cityLabel.trim()) ||
      (typeof tradeData.locationText === "string" && tradeData.locationText.trim()) ||
      "";

    const structuredTrade = {
      ...tradeData,

      ownerId,
      ownerUid: tradeData.ownerUid || ownerId,

      location: {
        city,
        cityLabel: city,
        country: (typeof loc.country === "string" && loc.country.trim()) || "Unknown",
        latitude: loc.latitude ?? null,
        longitude: loc.longitude ?? null,
      },

      // Canonical availability flag (new listings only)
      isAvailable: tradeData.isAvailable ?? true,

      createdAt: tradeData.createdAt ?? serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, "Trades"), structuredTrade);
    console.log("✅ Trade Listing Added with ID:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("❌ Error adding trade listing:", error);
    throw error;
  }
};

/**
 * Fetch all trades (fallback).
 * ALSO: filters out locked/accepted trades so UI never shows them.
 */
export const fetchPrivateTradeListings  = async () => {
  try {
    const snap = await getDocs(collection(db, "Trades"));
    const trades = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const available = trades.filter(isTradeAvailable);
    console.log("📦 Trades fetched:", trades.length, "| Available:", available.length);

    return available;
  } catch (error) {
    console.error("❌ Error fetching trade listings:", error);
    return [];
  }
};

/**
 * PUBLIC: Fetch all public trades
 * Reads from PublicTrades only (guest-safe)
 */
export const fetchPublicTradeListings = async ({ limitTo = 100 } = {}) => {
  try {
    const q = query(
      collection(db, "PublicTrades"),
      orderBy("createdAt", "desc"),
      limit(Math.min(limitTo, 200))
    );

    const snap = await getDocs(q);
    const trades = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    return trades.filter(isTradeAvailable);
  } catch (error) {
    console.error("❌ Error fetching public trade listings:", error);
    return [];
  }
};

/**
 * PUBLIC: Fetch sponsored trades (projection-safe)
 * Relies on PublicTrades.promotions.topAd
 */
export const fetchPublicSponsoredTrades = async () => {
  const toDateSafe = (v) => {
    if (!v) return null;
    if (typeof v?.toDate === "function") return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number" && Number.isFinite(v)) return new Date(v);
    return null;
  };

  const toDaysSafe = (v) => {
    const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
 const isActiveTopAd = (trade, nowMs) => {
    const promo = trade?.promotions || {};

    const explicitUntil =
      toDateSafe(promo.topAdUntil) ||
      toDateSafe(promo.sponsoredUntil) ||
      toDateSafe(trade?.topAdUntil) ||
      toDateSafe(trade?.sponsoredUntil);

    if (explicitUntil) {
      return nowMs <= explicitUntil.getTime();
    }

    const days =
      toDaysSafe(promo.topAdDays) ||
      toDaysSafe(promo.sponsoredDays) ||
      toDaysSafe(trade?.topAdDays) ||
      toDaysSafe(trade?.sponsoredDays) ||
      toDaysSafe(promo.topAd);

    const createdAtDate = toDateSafe(trade?.createdAt);
    if (!days || !createdAtDate) return false;

    const expiryMs = createdAtDate.getTime() + days * 24 * 60 * 60 * 1000;
    return nowMs <= expiryMs;
  };

  try {
    const nowMs = Date.now();

    const snap = await getDocs(collection(db, "PublicTrades"));

    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter(isTradeAvailable)
      .filter((t) => isActiveTopAd(t, nowMs));
  } catch (error) {
    console.error("❌ Error fetching public sponsored trades:", error);
    return [];
  }
};

// Backwards-compatible alias (legacy imports)
export const fetchTradeListings = fetchPrivateTradeListings;

